import { useCallback, useMemo } from "react";
import * as React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { isEmpty, startCase } from "lodash-es";

import { Typography } from "@smartrent/ui";
import {
  Online,
  Signal,
  Connection,
  WireDisconnected,
  InformationSolid,
  X,
  Hub,
  Person,
  XCircle,
} from "@smartrent/icons";
import { useModalState } from "@smartrent/hooks";

import { EM_DASH } from "@/utils/chars";
import { useIsDesktop } from "@/lib/hooks";
import helpers from "@/lib/helpers";

import { useAccountQuery } from "@/api/zipato";
import { useCellStrengthQuery, useHub } from "@/api/hub";
import { useUnit } from "@/api/units";

import { Badge } from "@/_v2/components/ui/badge";
import { useDrawerNav } from "@/common/AppDrawer";
import { CopyText } from "@/common/CopyText";

import { BackupAccessCodes } from "./BackupAccessCodes";
export interface OrgHeaderProps {
  serial: string;
  noHubAccount: boolean;
}

export const OrgHeader: React.FC<React.PropsWithChildren<OrgHeaderProps>> = ({
  serial,
  noHubAccount,
}) => {
  const isDesktop = useIsDesktop();
  const drawer = useDrawerNav();
  const {
    visible: drawerVisible,
    onClose: onDrawerClose,
    onToggle: onDrawerToggle,
  } = useModalState();

  const { data: hub } = useHub({ serial });

  const { data: unit } = useUnit(hub?.unit_id);

  const { data: zipato } = useAccountQuery(
    { serial },
    { enabled: hub?.type === "zipato" }
  );
  const { data: cellStrengthQuery, isLoading: cellStrengthLoading } =
    useCellStrengthQuery({ serial });

  const organization = useMemo(() => unit?.organization ?? null, [unit]);

  const isInstalledOnUnit = useMemo(
    () => !isEmpty(hub) && hub?.deleted_at === null,
    [hub]
  );

  const cellStrength = useMemo(
    () => cellStrengthQuery?.cellStrength,
    [cellStrengthQuery]
  );

  const cellStrengthColor = useMemo(() => {
    if (cellStrengthLoading) {
      return "secondary";
    }

    switch (cellStrength) {
      case null:
        return "secondary";
      case "MARGINAL":
        return "destructive";
      case "OK":
        return "destructive";
      case "GOOD":
        return "info";
      case "EXCELLENT":
        return "success";
      default:
        return "default";
    }
  }, [cellStrength, cellStrengthLoading]);

  const openDrawer = useCallback(() => {
    drawer.push("hubSettings", { zipato });
  }, [drawer, zipato]);

  const hubType = hub ? (
    <Badge
      // style={[styles.hubType, !isDesktop ? styles.topMargin : null]}
      icon={Hub}
      variant="secondary"
    >
      {helpers.getHubType(hub) === "unknown"
        ? "Unknown Hub Type"
        : startCase(helpers.getHubType(hub))}
    </Badge>
  ) : null;

  return (
    <View style={styles.container}>
      {isDesktop ? (
        <img
          src={organization?.logo_url ?? "/images/no-image.png"}
          alt="org-header"
          style={orgImageStyles}
        />
      ) : null}

      <View style={styles.orgInfo}>
        <Typography type="title">{organization?.name}</Typography>

        <View style={styles.infoContainer}>
          <View style={styles.infoRow}>
            <Typography type="title4">Serial:</Typography>

            <Typography style={styles.dataLink} color="textDisabled">
              {serial ?? EM_DASH}
            </Typography>

            <CopyText text={serial ?? EM_DASH} />

            {isDesktop ? hubType : null}
          </View>

          {hub?.type === "zipato" ? (
            <TouchableOpacity style={styles.infoRow} onPress={openDrawer}>
              <Typography type="title5" color="primary">
                Show Zipato Account Info
              </Typography>
            </TouchableOpacity>
          ) : null}
          {isInstalledOnUnit ? (
            <TouchableOpacity style={styles.infoRow} onPress={onDrawerToggle}>
              <Typography type="title5" color="primary">
                Show Backup Codes
              </Typography>
            </TouchableOpacity>
          ) : null}
        </View>

        {isInstalledOnUnit ? (
          <View style={styles.pillsRow}>
            {hub?.online ? (
              <Badge
                icon={Online}
                variant="success"
                className="mr-[8px] mt-[8px]"
              >
                Online
              </Badge>
            ) : (
              <Badge
                icon={hub ? WireDisconnected : X}
                variant={hub ? "destructive" : "secondary"}
                className="mr-[8px] mt-[8px]"
              >
                {hub ? "Offline" : "No Unit Found"}
              </Badge>
            )}

            {hub?.connection !== "ethernet" &&
            !cellStrengthLoading &&
            cellStrength ? (
              <Badge
                icon={Signal}
                variant={cellStrengthColor}
                className="mr-[8px] mt-[8px]"
              >
                {startCase(cellStrength) ?? "Unknown Cell Strength"}
              </Badge>
            ) : null}

            <Badge
              icon={Connection}
              variant="secondary"
              className="mr-[8px] mt-[8px]"
            >
              {[undefined, null, "unknown"].includes(hub?.connection)
                ? "Unknown Connection Type"
                : startCase(hub?.connection)}
            </Badge>
          </View>
        ) : (
          <View style={styles.pillsCol}>
            <Badge icon={X} variant="secondary" className="mb-[8px]">
              No Unit Found
            </Badge>
            <Badge icon={InformationSolid} variant="info" className="mb-[8px]">
              Please run Box Get & Box Session
              {"\n"}
              to view the network connection status.
            </Badge>
            <Badge
              className="mb-[8px]"
              icon={noHubAccount ? XCircle : Person}
              variant="secondary"
            >
              {noHubAccount ? "No Hub Account" : "Hub Account Found"}
            </Badge>
          </View>
        )}
      </View>
      {isInstalledOnUnit ? (
        <BackupAccessCodes
          open={drawerVisible}
          onClose={onDrawerClose}
          hub={hub}
        />
      ) : null}
    </View>
  );
};

const orgImageStyles = { maxWidth: 184, borderRadius: 4 };

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
  },
  orgInfo: {
    marginLeft: 24,
    height: 184,
    justifyContent: "space-between",
    maxWidth: "100%",
  },
  infoContainer: {
    marginTop: 24,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  dataLink: {
    marginHorizontal: 8,
    alignItems: "center",
  },
  pillsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  pillRow: {
    marginRight: 8,
    marginTop: 8,
  },
  pillsCol: {
    alignItems: "flex-start",
    marginTop: 24,
  },
  pillCol: {
    marginBottom: 8,
  },
  hubType: { marginLeft: 8 },
  topMargin: { marginTop: 8 },
});
